// src/utils/conversationUtils.js

/**
 * Generates a preview string from a conversation object.
 * It first checks for a dedicated 'first_user_message_preview' field.
 * If not found or empty, it falls back to parsing the 'messages' array
 * to find the first user prompt, cleaning it of context information.
 *
 * @param {object} conversation - The conversation object.
 * @returns {string} A preview string, or a message indicating unavailability/error.
 */
export const getConversationPreview = (conversation) => {
    // Prefer the dedicated preview field if available
    if (conversation.first_user_message_preview) {
      const preview = String(conversation.first_user_message_preview).trim();
      if (preview) {
        return preview.length > 100 ? preview.substring(0, 97) + '...' : preview;
      }
    }
  
    // Fallback to parsing the 'messages' field
    if (!conversation.messages) {
      return 'No messages available for preview.';
    }
  
    let messagesArray;
    try {
      messagesArray = typeof conversation.messages === 'string'
        ? JSON.parse(conversation.messages)
        : conversation.messages;
    } catch (e) {
      console.warn(`Error parsing messages for preview (conversation ID: ${conversation.id}):`, e);
      return 'Error parsing messages.';
    }
  
    if (!Array.isArray(messagesArray)) {
      console.warn(`Invalid messages format for preview (conversation ID: ${conversation.id}): not an array.`, messagesArray);
      return 'Invalid messages format.';
    }
  
    for (const message of messagesArray) {
      if (message.kind === 'request' && Array.isArray(message.parts)) {
        for (const part of message.parts) {
          if (part.part_kind === 'user-prompt' && typeof part.content === 'string' && part.content.trim()) {
            let userMessageContent = part.content.trim();
  
            // 1. Attempt to remove JSON-like page context from the beginning
            const pageContextJsonRegex = /^(?:\[?\s*)?(\{"url":.*?\}(?:,\s*\{"url":.*?\})*)(?:\s*\]?)/is;
            const pageContextJsonMatch = userMessageContent.match(pageContextJsonRegex);
            
            if (pageContextJsonMatch && pageContextJsonMatch[1] && pageContextJsonMatch[1].trim() !== "") {
              try {
                JSON.parse(`[${pageContextJsonMatch[1]}]`);
                userMessageContent = userMessageContent.replace(pageContextJsonMatch[0], '').trim();
              } catch (jsonError) {
                // Not valid JSON, don't strip.
              }
            }
            
            // 2. Attempt to remove text-based "Page context: ..." from the beginning
            const pageContextTextRegex = /^Page context:.*?(User:|$)/is;
            if (pageContextTextRegex.test(userMessageContent)) { 
                userMessageContent = userMessageContent.replace(pageContextTextRegex, '$1').trim();
            }
  
            // 3. Remove "User: " prefix if it's at the beginning
            userMessageContent = userMessageContent.replace(/^User:\s*/i, '').trim();
  
            if (userMessageContent) {
              return userMessageContent.length > 100 ? userMessageContent.substring(0, 97) + '...' : userMessageContent;
            }
          }
        }
      }
    }
    return 'No user message for preview.';
  };
  
  /**
   * Formats a date string into a more readable locale string.
   * @param {string} dateString - The ISO date string.
   * @returns {string} Formatted date string.
   */
  export const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString(undefined, { 
        year: 'numeric', month: 'short', day: 'numeric', 
        hour: '2-digit', minute: '2-digit' 
      });
    } catch (e) {
      console.error("Error formatting date:", dateString, e);
      return 'Invalid Date';
    }
  };